import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, formatRelativeAge } from '../../lib/format';
import { STATUS_LABEL, statusTone } from '../../lib/pipeline';
import { platformLabel } from '../../lib/platforms';
import { monthOf } from '../calendar/calendarDates';
import { errorLabel } from '../calendar/automation';
import { PostPicture, PostTypeGlyph } from '../history/PostTile';
import { HISTORY_WAITING_PATH } from '../history/historyFilters';
import { postTitle } from '../history/postLabels';
import { NEEDS_YOU_VISIBLE, type NeedsYouItem } from './needsYouItems';
import { PanelLink } from './PanelLink';

interface NeedsYouProps {
  items: readonly NeedsYouItem[];
  /** The request page came back full: there may be more than were counted. */
  isCapped: boolean;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

/** The calendar opened on a plan's own day, in the list view. */
function planPath(scheduledOn: string): string {
  return `/calendar?month=${monthOf(scheduledOn)}&view=list&day=${scheduledOn}`;
}

function RequestRow({ item }: { item: Extract<NeedsYouItem, { kind: 'request' }> }) {
  const { request } = item;
  const isFailed = request.status === 'failed';
  const line = isFailed
    ? (request.failure_reason ?? request.caption_preview ?? postTitle(request))
    : postTitle(request);

  return (
    <li className="needs-row">
      <PostPicture
        path={request.thumbnail_path}
        className="needs-row__thumb"
        fallback={<PostTypeGlyph postType={request.post_type} />}
      />
      <div className="needs-row__main">
        <p className="needs-row__meta">
          <span className={`status-chip status-chip--${statusTone(request.status)}`}>
            {STATUS_LABEL[request.status]}
          </span>
          <span>
            {platformLabel(request.platform)} · {formatRelativeAge(request.created_at)}
          </span>
        </p>
        <p className="needs-row__line">{line}</p>
      </div>
      <Link className="button button--small needs-row__action" to={`/review/${request.id}`}>
        {isFailed ? 'Open' : 'Resume'}
        <span className="visually-hidden">: {postTitle(request)}</span>
      </Link>
    </li>
  );
}

function PlanRow({ item }: { item: Extract<NeedsYouItem, { kind: 'plan' }> }) {
  const { plan } = item;
  const isFailed = plan.automation_status === 'failed';
  const reason = errorLabel(plan.automation_error);

  return (
    <li className="needs-row">
      <span className="needs-row__thumb">
        <span className="post-glyph" aria-hidden="true">
          Plan
        </span>
      </span>
      <div className="needs-row__main">
        <p className="needs-row__meta">
          <span className={`status-chip status-chip--${isFailed ? 'failed' : 'waiting'}`}>
            {isFailed ? 'Plan failed' : 'Plan to review'}
          </span>
          <span>
            {plan.platform ? `${platformLabel(plan.platform)} · ` : ''}
            {formatDate(plan.scheduled_on)}
          </span>
        </p>
        <p className="needs-row__line">
          {plan.title}
          {reason ? ` — ${reason}` : ''}
        </p>
      </div>
      <Link className="button button--small needs-row__action" to={planPath(plan.scheduled_on)}>
        Open
        <span className="visually-hidden">: {plan.title}</span>
      </Link>
    </li>
  );
}

/**
 * Everything stopped and waiting on the user: runs at either review step,
 * failures from the last fortnight, and calendar plans that parked. The first
 * thing on Home, because it is the only part that is somebody's to-do.
 *
 * Empty, the section collapses to one line. Old waiting runs are shown, not
 * hidden: archiving them is a separate decision (UI refresh plan, out of scope).
 */
/**
 * The list's height when it holds more than {@link NEEDS_YOU_VISIBLE} rows: the
 * bottom of the last visible row, measured rather than assumed, because a row
 * grows when its meta line wraps on a narrow screen. Null means no cap.
 */
function useVisibleRowsHeight(rowCount: number) {
  const listRef = useRef<HTMLUListElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const isCapped = rowCount > NEEDS_YOU_VISIBLE;

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !isCapped) return;

    const measure = () => {
      const last = list.children[NEEDS_YOU_VISIBLE - 1];
      if (!(last instanceof HTMLElement)) return;
      const listTop = list.getBoundingClientRect().top - list.scrollTop;
      setHeight(Math.ceil(last.getBoundingClientRect().bottom - listTop));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [isCapped, rowCount]);

  return { listRef, maxHeight: isCapped ? height : null };
}

export function NeedsYou({ items, isCapped, isLoading, error, onRetry }: NeedsYouProps) {
  const count = `${items.length}${isCapped ? '+' : ''}`;
  const { listRef, maxHeight } = useVisibleRowsHeight(items.length);

  if (!isLoading && !error && items.length === 0) {
    return (
      <section className="home-panel home-panel--quiet" aria-labelledby="needs-you-title">
        <h2 className="home-panel__title" id="needs-you-title">
          Needs you
        </h2>
        <p className="home-panel__empty">Nothing waiting. Nice.</p>
      </section>
    );
  }

  return (
    <section className="home-panel" aria-labelledby="needs-you-title">
      <div className="home-panel__head">
        <h2 className="home-panel__title" id="needs-you-title">
          Needs you{isLoading ? '' : ` (${count})`}
        </h2>
        <PanelLink to={HISTORY_WAITING_PATH}>View all</PanelLink>
      </div>

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Checking what is waiting
        </p>
      ) : null}

      {error ? (
        <p className="home-panel__error">
          <span role="alert">{error}</span>{' '}
          <button className="link-button" type="button" onClick={onRetry}>
            Try again
          </button>
        </p>
      ) : null}

      {items.length > 0 ? (
        <ul
          ref={listRef}
          className={
            items.length > NEEDS_YOU_VISIBLE ? 'needs-list needs-list--scroll' : 'needs-list'
          }
          style={maxHeight === null ? undefined : { maxHeight }}
        >
          {items.map((item) =>
            item.kind === 'request' ? (
              <RequestRow key={item.id} item={item} />
            ) : (
              <PlanRow key={item.id} item={item} />
            ),
          )}
        </ul>
      ) : null}
    </section>
  );
}
